"use client";

import { useState } from "react";
import { UserProfile } from "@/utils/types";
import { issueSep10LinkChallenge, verifySep10Link } from "@/services/stellar.service";
import { useWallet } from "@/contexts/WalletContext";

interface StellarWalletsSectionProps {
  profileData: UserProfile | null;
  onProfileUpdated: () => void;
}

const ghostButtonStyle: React.CSSProperties = {
  color: "#888",
  background: "#111",
  border: "1px solid #1e1e1e",
  borderRadius: "8px",
  padding: "9px 16px",
  display: "flex",
  alignItems: "center",
  gap: "7px",
  cursor: "pointer",
  fontSize: "11px",
  fontWeight: 500,
  letterSpacing: "0.06em",
  transition: "border-color 0.15s, color 0.15s",
};

export function StellarWalletsSection({ profileData, onProfileUpdated }: StellarWalletsSectionProps) {
  const { isConnected: walletConnected, address: walletAddress, signTransaction } = useWallet();
  const [linkingStellar, setLinkingStellar] = useState(false);
  const [stellarError, setStellarError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"TESTNET" | "PUBLIC">("TESTNET");

  async function handleLinkStellar(network: "PUBLIC" | "TESTNET") {
    if (!walletConnected || !walletAddress) {
      setStellarError("Connect your Stellar wallet before linking.");
      return;
    }
    if (!signTransaction) {
      setStellarError("Wallet does not support transaction signing.");
      return;
    }

    try {
      setStellarError(null);
      setLinkingStellar(true);
      const challenge = await issueSep10LinkChallenge({ network, address: walletAddress });
      const signedXDR = await signTransaction(challenge.txXDR, challenge.networkPassphrase);
      await verifySep10Link({ challengeId: challenge.challengeId, address: walletAddress, txXDR: signedXDR });
      onProfileUpdated();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to link Stellar account";
      setStellarError(message);
    } finally {
      setLinkingStellar(false);
    }
  }

  const accounts = profileData?.stellarAccounts ?? [];
  const filteredAccounts = accounts.filter((a) => a.network === activeTab);

  return (
    <div
      style={{
        background: "#0e0e0e",
        border: "1px solid #1c1c1c",
        borderRadius: "14px",
        overflow: "hidden",
        marginBottom: "10px",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          padding: "18px 20px",
          borderBottom: "1px solid #1c1c1c",
        }}
      >
        <div>
          <p style={{ fontSize: "12px", fontWeight: 600, letterSpacing: "0.08em", color: "#c8c3ba", margin: 0 }}>
            STELLAR WALLETS
          </p>
          <p style={{ fontSize: "10px", color: "#4a4a4a", letterSpacing: "0.02em", marginTop: "2px" }}>
            Linked via SEP-10 challenge signing
          </p>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: "18px 20px" }}>
        {/* Tab group */}
        <div
          style={{
            display: "inline-flex",
            background: "#0a0a0a",
            border: "1px solid #1c1c1c",
            borderRadius: "8px",
            padding: "3px",
            marginBottom: "16px",
          }}
        >
          {(["TESTNET", "PUBLIC"] as const).map((net) => (
            <button
              key={net}
              onClick={() => { setActiveTab(net); }}
              style={{
                fontSize: "10px",
                padding: "6px 14px",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: 500,
                letterSpacing: "0.06em",
                transition: "background 0.15s, color 0.15s, border-color 0.15s",
                background: activeTab === net ? "#1a1a1a" : "transparent",
                color: activeTab === net ? "#d4cfc6" : "#4a4a4a",
                border: activeTab === net ? "1px solid #222" : "1px solid transparent",
              }}
            >
              {net}
            </button>
          ))}
        </div>

        {/* Connect & link */}
        <div style={{ marginBottom: "16px" }}>
          <button
            onClick={() => { void handleLinkStellar(activeTab); }}
            disabled={linkingStellar}
            style={{
              ...ghostButtonStyle,
              opacity: linkingStellar ? 0.6 : 1,
              cursor: linkingStellar ? "not-allowed" : "pointer",
              marginBottom: "8px",
            }}
            onMouseEnter={(e) => {
              if (!linkingStellar) {
                e.currentTarget.style.borderColor = "#272727";
                e.currentTarget.style.color = "#d4cfc6";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#1e1e1e";
              e.currentTarget.style.color = "#888";
            }}
          >
            <span style={{ fontSize: "13px", lineHeight: 1 }}>&#43;</span>
            {linkingStellar ? "Linking..." : "Connect & Link Wallet"}
          </button>
          <p style={{ fontSize: "10px", color: "#333" }}>
            {walletConnected
              ? "Make sure your wallet is unlocked to sign the challenge."
              : "Connect your Freighter wallet before linking."}
          </p>
        </div>

        {stellarError && (
          <p style={{ fontSize: "11px", color: "#a85555", marginBottom: "12px" }}>
            {stellarError}
          </p>
        )}

        {filteredAccounts.length === 0 ? (
          <p style={{ fontSize: "11px", color: "#333" }}>
            No {activeTab.toLowerCase()} accounts linked yet
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {filteredAccounts.map((acct) => {
              const linked = acct.linkedAt ? new Date(acct.linkedAt).toLocaleString() : "";
              const verified = acct.lastVerifiedAt ? new Date(acct.lastVerifiedAt).toLocaleString() : null;
              return (
                <div
                  key={`${acct.network}-${acct.address}`}
                  style={{
                    padding: "10px 14px",
                    background: "#0a0a0a",
                    border: "1px solid #1c1c1c",
                    borderRadius: "8px",
                  }}
                >
                  <p
                    style={{
                      fontFamily: "monospace",
                      fontSize: "11px",
                      color: "#c8c3ba",
                      wordBreak: "break-all",
                      margin: 0,
                    }}
                  >
                    {acct.address}
                  </p>
                  <p style={{ fontSize: "10px", color: "#333", marginTop: "4px" }}>
                    Linked {linked}
                    {verified ? ` \u2022 Verified ${verified}` : ""}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
